import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { FilterSidebar } from '../components/property/FilterSidebar';
import { InteractiveMap } from '../components/property/InteractiveMap';
import { Pagination } from '../components/common/Pagination';
import { ShareButtons } from '../components/property/ShareButtons';
import { Auction } from '../types';
import { REGIONS } from '../data/mockData';
import { useToast } from '../components/common/Toast';
import { Loader2 } from 'lucide-react';

export function AuctionsPage() {
  const { auctions, filters, clearFilters, isAuthenticated, user, propertiesLoading, propertiesLoaded, loadPropertiesFromApi } = useAppStore();
  const { language, t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const [filter, setFilter] = useState<'all' | 'ending-soon'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Always load fresh data from API on mount (auctions can expire/get extended)
  useEffect(() => {
    if (!propertiesLoading) {
      loadPropertiesFromApi();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortOptions = [
    { value: 'newest', label: language === 'sr' ? 'Najnovije' : 'Newest' },
    { value: 'ending', label: language === 'sr' ? 'Uskoro završava' : 'Ending soon' },
    { value: 'price-asc', label: language === 'sr' ? 'Cena: rastuće' : 'Price: Low to High' },
    { value: 'price-desc', label: language === 'sr' ? 'Cena: opadajuće' : 'Price: High to Low' },
    { value: 'sqm-asc', label: language === 'sr' ? 'Kvadratura: rastuće' : 'Area: Low to High' },
    { value: 'sqm-desc', label: language === 'sr' ? 'Kvadratura: opadajuće' : 'Area: High to Low' },
    { value: 'views', label: language === 'sr' ? 'Najpopularnije' : 'Most Popular' },
  ];

  // Calculate focusLocation based on selected locations
  const focusLocation = useMemo(() => {
    // If we have locations array, use the first one
    if (filters.locations && filters.locations.length > 0) {
      const firstLoc = filters.locations[0];
      if (firstLoc.lat && firstLoc.lng) {
        return { lat: firstLoc.lat, lng: firstLoc.lng, zoom: firstLoc.zoom || 11 };
      }
    }
    // Fallback to legacy locationLat/locationLng
    if (filters.locationLat && filters.locationLng) {
      return { lat: filters.locationLat, lng: filters.locationLng, zoom: 13 };
    }
    if (filters.region) {
      const region = REGIONS.find(r => r.name === filters.region);
      if (region) {
        if (filters.city) {
          const city = region.cities.find(c => c.name === filters.city);
          if (city?.lat && city?.lng) {
            return { lat: city.lat, lng: city.lng, zoom: 13 };
          }
        }
        if (region.lat && region.lng) {
          return { lat: region.lat, lng: region.lng, zoom: region.zoom || 10 };
        }
      }
    }
    return undefined;
  }, [filters.locations, filters.region, filters.city, filters.locationLat, filters.locationLng]);

  const getTimeRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return diff;
  };

  // Filter only active, approved auctions that haven't ended
  const activeAuctions = auctions.filter(a => {
    if (a.status !== 'active' || !a.approved) return false;
    if (a.auctionEndsAt && new Date(a.auctionEndsAt).getTime() < Date.now()) return false;
    return true;
  });

  // Apply category filters
  let filteredAuctions = activeAuctions.filter(a => {
    if (filters.category && a.category !== filters.category) return false;
    if (filters.subcategory && a.subcategory !== filters.subcategory) return false;
    
    // Handle multiple locations filter
    if (filters.locations && filters.locations.length > 0) {
      const matchesLocation = filters.locations.some(loc => {
        if (loc.type === 'region') {
          return a.location.region === loc.name;
        } else if (loc.type === 'subregion') {
          return a.location.city === loc.name || a.location.region === loc.region;
        } else if (loc.type === 'listing-address') {
          // For listing-address, filter to the specific auction ID
          if (loc.propertyId) {
            return a.id === loc.propertyId;
          }
          return loc.subregion ? a.location.city === loc.subregion : false;
        } else if (loc.type === 'address') {
          // For OSM address: filter by subregion
          if (loc.subregion) {
            return a.location.city === loc.subregion || 
                   a.location.city.toLowerCase().includes(loc.subregion.toLowerCase()) ||
                   loc.subregion.toLowerCase().includes(a.location.city.toLowerCase());
          }
          const itemAddress = `${a.location.address || ''} ${a.location.city} ${a.location.region}`.toLowerCase();
          return itemAddress.includes(loc.name.split(',')[0].toLowerCase());
        }
        return false;
      });
      if (!matchesLocation) return false;
    } else {
      // Fallback to single region/city filter
      if (filters.region && a.location.region !== filters.region) return false;
      if (filters.city && a.location.city !== filters.city) return false;
    }
    
    if (filters.priceMin && a.currentPrice < filters.priceMin) return false;
    if (filters.priceMax && a.currentPrice > filters.priceMax) return false;
    
    // Only with images filter
    if (filters.onlyWithImages) {
      const validImages = a.images?.filter(img => img && img.trim() !== '') || [];
      if (validImages.length === 0) return false;
    }
    
    // Urgent only filter
    if (filters.urgentOnly && !(a as any).isUrgent) return false;
    
    return true;
  });

  // Apply time filter
  if (filter === 'ending-soon') {
    filteredAuctions = filteredAuctions.filter(a => 
      getTimeRemaining(a.auctionEndsAt) < 7 * 24 * 60 * 60 * 1000 && 
      getTimeRemaining(a.auctionEndsAt) > 0
    );
  }

  // Sort: urgent listings first
  const urgentSortedAuctions = [...filteredAuctions].sort((a, b) => {
    if ((a as any).isUrgent && !(b as any).isUrgent) return -1;
    if (!(a as any).isUrgent && (b as any).isUrgent) return 1;
    return 0;
  });

  const sortedAuctions = [...urgentSortedAuctions].sort((a, b) => {
    switch (sortBy) {
      case 'ending':
        return getTimeRemaining(a.auctionEndsAt) - getTimeRemaining(b.auctionEndsAt);
      case 'price-asc':
        return a.currentPrice - b.currentPrice;
      case 'price-desc':
        return b.currentPrice - a.currentPrice;
      case 'sqm-asc':
        return a.squareMeters - b.squareMeters;
      case 'sqm-desc':
        return b.squareMeters - a.squareMeters;
      case 'views':
        return b.views - a.views;
      default:
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
  });

  const hasActiveFilters = Object.keys(filters).length > 0;

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy, filter]);

  // Pagination
  const totalItems = sortedAuctions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedAuctions = viewMode === 'map' ? sortedAuctions : sortedAuctions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {language === 'sr' ? 'Aukcije nekretnina' : 'Property Auctions'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {language === 'sr' 
              ? 'Licitirajte za nekretnine po povoljnim cenama' 
              : 'Bid on properties at competitive prices'}
          </p>
        </div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
            {language === 'sr' ? 'Kako funkcionišu aukcije?' : 'How do auctions work?'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                  {language === 'sr' ? 'Pregledajte aukcije' : 'Browse auctions'}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'sr' 
                    ? 'Pronađite nekretninu koja vam odgovara i pregledajte sve detalje' 
                    : 'Find a property that suits you and review all details'}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                  {language === 'sr' ? 'Postavite ponudu' : 'Place a bid'}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'sr' 
                    ? 'Licitirajte iznos koji ste spremni da platite za nekretninu' 
                    : 'Bid the amount you are willing to pay for the property'}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                  {language === 'sr' ? 'Pobedite na aukciji' : 'Win the auction'}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'sr' 
                    ? 'Najviša ponuda na kraju aukcije osvaja nekretninu' 
                    : 'The highest bid at the end of the auction wins the property'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar - Desktop */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <FilterSidebar 
              showAdType={false} 
              showPricePerUnit={true}
              listings={auctions}
              pageType="auctions"
            />
          </div>

          {/* Mobile Filters Button */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-lg shadow-md"
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="text-slate-700 dark:text-slate-300">{t.filters.title}</span>
              {hasActiveFilters && (
                <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {Object.keys(filters).length}
                </span>
              )}
            </button>
            {showFilters && (
              <div className="mt-4">
                <FilterSidebar 
                  showAdType={false} 
                  showPricePerUnit={true} 
                  onClose={() => setShowFilters(false)}
                  listings={auctions}
                  pageType="auctions"
                />
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                {/* Auction filter tabs */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      filter === 'all'
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {language === 'sr' ? 'Sve aukcije' : 'All auctions'}
                  </button>
                  <button
                    onClick={() => setFilter('ending-soon')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      filter === 'ending-soon'
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {language === 'sr' ? 'Uskoro završavaju' : 'Ending soon'}
                  </button>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-amber-600 hover:text-amber-500 text-sm"
                  >
                    {t.filters.clearFilters}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                <span className="text-slate-600 dark:text-slate-400">
                  {sortedAuctions.length} {language === 'sr' ? 'aukcija' : 'auctions'}
                </span>

                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Mreža' : 'Grid'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Lista' : 'List'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'map' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Mapa' : 'Map'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Map View */}
            {viewMode === 'map' && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden mb-6">
                <InteractiveMap
                  properties={filteredAuctions}
                  height="500px"
                  showFilters={false}
                  focusLocation={focusLocation}
                  selectedRegion={filters.region}
                />
              </div>
            )}

            {/* Loading State */}
            {propertiesLoading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                <span className="ml-3 text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Učitavanje...' : 'Loading...'}
                </span>
              </div>
            )}

            {/* Auctions Grid/List */}
            {!propertiesLoading && sortedAuctions.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center"
              >
                <svg className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {language === 'sr' ? 'Nema aktivnih aukcija' : 'No active auctions'}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {language === 'sr'
                    ? 'Trenutno nema aukcija koje odgovaraju vašim kriterijumima'
                    : 'Currently no auctions match your criteria'}
                </p>
              </motion.div>
            ) : viewMode === 'list' ? (
              <div className="flex flex-col gap-4">
                {paginatedAuctions.map((auction) => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    language={language}
                    isAuthenticated={isAuthenticated}
                    isVerified={user?.emailVerified || user?.verified}
                    variant="horizontal"
                  />
                ))}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {paginatedAuctions.map((auction) => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    language={language}
                    isAuthenticated={isAuthenticated}
                    isVerified={user?.emailVerified || user?.verified}
                  />
                ))}
              </div>
            ) : null}

            {/* Pagination */}
            {viewMode !== 'map' && sortedAuctions.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalItems}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AuctionCard({ auction, language, isAuthenticated, isVerified, variant = 'default' }: {
  auction: Auction;
  language: 'sr' | 'en';
  isAuthenticated: boolean;
  isVerified?: boolean;
  variant?: 'default' | 'horizontal';
}) {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useAppStore();
  const toast = useToast();
  const favoriteStatus = isFavorite(auction.id);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }
    try {
      await toggleFavorite(auction.id);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const getTimeRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return { text: language === 'sr' ? 'Završeno' : 'Ended', urgent: false };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return { text: `${days}d ${hours}h`, urgent: days < 3 };
    if (hours > 0) return { text: `${hours}h ${minutes}m`, urgent: true };
    return { text: `${minutes}m`, urgent: true };
  };

  const timeLeft = getTimeRemaining(auction.auctionEndsAt);

  const handleBidClick = () => {
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }
    if (!isVerified) {
      toast.warning(
        language === 'sr' ? 'Verifikacija potrebna' : 'Verification needed',
        language === 'sr' ? 'Molimo prvo verifikujte vašu email adresu' : 'Please verify your email first'
      );
      return;
    }
    navigate(`/aukcije/${auction.id}`);
  };

  // Horizontal (list) variant
  if (variant === 'horizontal') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col sm:flex-row sm:h-[200px]"
      >
        <Link to={`/aukcije/${auction.id}`} className="sm:w-64 h-48 sm:h-full flex-shrink-0 relative">
          {auction.images[0] ? (
            <img src={auction.images[0]} alt={auction.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full min-h-[12rem] bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-medium ${
            timeLeft.urgent ? 'bg-red-500 text-white' : 'bg-slate-900/80 text-white'
          }`}>
            {timeLeft.text}
          </div>
        </Link>
        <div className="p-4 flex-1 flex flex-col overflow-hidden">
          <div className="flex items-start justify-between gap-2 mb-1">
            <Link to={`/aukcije/${auction.id}`}>
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white hover:text-amber-600 transition-colors line-clamp-1">
                {auction.title}
              </h3>
            </Link>
            <ShareButtons
              url={`/aukcije/${auction.id}`}
              title={auction.title}
              description={auction.description}
            />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
            {auction.location.city}, {auction.location.region}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 line-clamp-2 hidden sm:block">
            {auction.description}
          </p>
          <div className="mt-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-6">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'Početna cena' : 'Starting price'}
                </p>
                <p className="text-slate-600 dark:text-slate-400">{auction.startingPrice.toLocaleString()} EUR</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'Trenutna cena' : 'Current price'}
                </p>
                <p className="text-xl font-bold text-amber-600">{auction.currentPrice.toLocaleString()} EUR</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'Ponuda' : 'Bids'}
                </p>
                <p className="text-slate-600 dark:text-slate-400">{auction.bids.length}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleToggleFavorite}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  favoriteStatus 
                    ? 'bg-red-100 text-red-600 dark:bg-red-900/20' 
                    : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
                title={favoriteStatus ? 'Ukloni iz omiljenih' : 'Dodaj u omiljene'}
              >
                {favoriteStatus ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                )}
              </button>
              <button
                onClick={handleBidClick}
                className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Licitiraj' : 'Bid'}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Default (grid) variant
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
    >
      <Link to={`/aukcije/${auction.id}`}>
        <div className="relative">
          {auction.images[0] ? (
            <img src={auction.images[0]} alt={auction.title} className="w-full h-48 object-cover" />
          ) : (
            <div className="w-full h-48 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-medium ${
            timeLeft.urgent ? 'bg-red-500 text-white' : 'bg-slate-900/80 text-white'
          }`}>
            {timeLeft.text}
          </div>
        </div>
      </Link>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Link to={`/aukcije/${auction.id}`}>
            <h3 className="font-semibold text-slate-900 dark:text-white hover:text-amber-600 transition-colors">
              {auction.title}
            </h3>
          </Link>
          <ShareButtons
            url={`/aukcije/${auction.id}`}
            title={auction.title}
            description={auction.description}
          />
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
          {auction.location.city}, {auction.location.region}
        </p>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Početna cena' : 'Starting price'}
            </p>
            <p className="text-slate-600 dark:text-slate-400">{auction.startingPrice.toLocaleString()} EUR</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Trenutna cena' : 'Current price'}
            </p>
            <p className="text-xl font-bold text-amber-600">{auction.currentPrice.toLocaleString()} EUR</p>
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {auction.bids.length} {language === 'sr' ? 'ponuda' : 'bids'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleToggleFavorite}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                favoriteStatus 
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/20' 
                  : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
              title={favoriteStatus ? 'Ukloni iz omiljenih' : 'Dodaj u omiljene'}
            >
              {favoriteStatus ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              )}
            </button>
            <button
              onClick={handleBidClick}
              className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
            >
              {language === 'sr' ? 'Licitiraj' : 'Bid'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
